import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import StandardDatePicker from "./StandardDatePicker";
import { getFlashNewsById, saveFlashNews, updateFlashNews } from "../Apis/FlashNewsAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { toast } from "react-toastify";
import { uploadFile } from "../Apis/ManagerAPIs";
import { getImageUrl } from "../Apis/api";

const FlashNewsFormLayer = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        name: "",
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        image: null,
        targetSegment: "3",
        targetLocations: [],
        visitorType: "3",
    });
    
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [locationOptions, setLocationOptions] = useState([]);

    const segmentOptions = [
        { value: "1", label: "B2B" },
        { value: "2", label: "B2C" },
        { value: "3", label: "Both" },
    ];

    const visitorTypeOptions = [
        { value: "1", label: "Exhibitor" },
        { value: "2", label: "Visitor" },
        { value: "3", label: "Both" },
    ];

    useEffect(() => {
        const fetchInitialData = async () => {
            const locRes = await getLocations();
            if (locRes.status) {
                const locData = locRes.response?.data || locRes.response;
                setLocationOptions(Array.isArray(locData) ? locData.map(l => ({ value: l._id || l.id, label: l.name })) : []);
            }

            if (isEdit) {
                setLoading(true);
                const res = await getFlashNewsById(id);
                if (res.status && res.response) {
                    const data = res.response?.data || res.response;
                    const segmentMap = { "B2B": "1", "B2C": "2", "Both": "3" };
                    const visitorMap = { "Exhibitor": "1", "Visitor": "2", "Both": "3" };

                    setFormData({
                        name: data.name || "",
                        title: data.title || "",
                        description: data.description || "",
                        startDate: data.startDate || "",
                        endDate: data.endDate || "",
                        image: data.image || null,
                        targetSegment: segmentMap[data.targetSegment] || data.targetSegment?.toString() || "3",
                        targetLocations: Array.isArray(data.targetLocations) 
                            ? data.targetLocations.map(l => typeof l === 'object' ? (l._id || l.id) : l) 
                            : [],
                        visitorType: visitorMap[data.visitorType] || data.visitorType?.toString() || "3",
                    });
                }
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [isEdit, id]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (name, val) => {
        if (Array.isArray(val)) {
            setFormData(prev => ({ ...prev, [name]: val.map(v => v.value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: val ? val.value : "" }));
        }
    };

    const handleFileChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const formDataUpload = new FormData();
            formDataUpload.append("file", file);
            formDataUpload.append("folder", "flash-news");
            const res = await uploadFile(formDataUpload);
            if (res.status) {
                const imagePath = res.response?.data?.path || res.response;
                setFormData((prev) => ({ ...prev, image: imagePath }));
                if (errors.image) setErrors(prev => ({ ...prev, image: "" }));
                toast.success("Image Uploaded Successfully");
            }
        }
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is Required";
        if (!formData.title.trim()) newErrors.title = "Title is Required";
        if (!formData.description.trim()) newErrors.description = "Description is Required";
        if (!formData.startDate) newErrors.startDate = "Start Date is Required";
        if (!formData.endDate) newErrors.endDate = "End Date is Required";
        if (!formData.image) newErrors.image = "Image is Required";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        setSubmitting(true);
        const payload = {
            ...formData,
            targetLocations: formData.targetLocations
        };

        let res;
        if (isEdit) {
            res = await updateFlashNews(id, payload);
        } else {
            res = await saveFlashNews(payload);
        }

        if (res.status) {
            toast.success(`Flash News ${isEdit ? "Updated" : "Added"} Successfully`);
            navigate("/flash-news");
        }
        setSubmitting(false);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom px-24 py-16 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(135deg, #000000ff, #4f1818ff, #ff0000ff)" }}>
                <h6 className="text-white mb-0" style={{ fontWeight: "700" }}>{isEdit ? "EDIT NEWS" : "ADD NEWS"}</h6>
                <Link to="/flash-news" className="btn btn-sm d-flex align-items-center gap-1" style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}>
                    <Icon icon="solar:arrow-left-bold" /> List News
                </Link>
            </div>

            <div className="card-body p-24">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="row gy-4">
                        {/* Name */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="name"
                                className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Enter Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                autoComplete="off"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        {/* Title */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Title <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="title"
                                className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                placeholder="Enter Title"
                                value={formData.title}
                                onChange={handleInputChange}
                                autoComplete="off"
                            />
                            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-bold mb-8">Start Date <span className="text-danger">*</span></label>
                            <StandardDatePicker
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleInputChange}
                                minDate="today"
                            />
                            {errors.startDate && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.startDate}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-bold mb-8">End Date <span className="text-danger">*</span></label>
                            <StandardDatePicker
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleInputChange}
                                minDate={formData.startDate || "today"}
                            />
                            {errors.endDate && <div className="text-danger mt-1" style={{ fontSize: "0.875em" }}>{errors.endDate}</div>}
                        </div>

                        {/* Description */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold mb-8">Description <span className="text-danger">*</span></label>
                            <textarea
                                name="description"
                                rows="5"
                                className={`form-control radius-8 ${errors.description ? 'is-invalid' : ''}`}
                                placeholder="Enter Description"
                                value={formData.description}
                                onChange={handleInputChange}
                                style={{ height: '140px' }}
                            ></textarea>
                            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                        </div>

                        {/* Image Upload */}
                        <div className="col-md-6">
                            <label className="form-label fw-bold mb-8">Image Upload <span className="text-danger-600">*</span></label>
                            <div 
                                className={`upload-image-box border radius-12 d-flex flex-column justify-content-center align-items-center bg-base hover-border-primary-600 transition-2 ${errors.image ? 'border-danger' : ''}`}
                                style={{ height: '140px', cursor: 'pointer', borderStyle: 'dashed' }}
                                onClick={() => fileInputRef.current.click()}
                            >
                                {formData.image ? (
                                    <img src={getImageUrl(formData.image)} alt="Preview" style={{ maxWidth: '100%', maxHeight: '130px', objectFit: 'contain', padding: '10px' }} />
                                ) : (
                                    <>
                                        <Icon icon="solar:upload-minimalistic-linear" className="text-4xl text-primary-600 mb-8" />
                                        <span className="text-secondary-light fw-medium">Click to upload image</span>
                                    </>
                                )}
                            </div>
                            {errors.image && <div className="text-danger-600 text-xs mt-1">{errors.image}</div>}
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="d-none"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>

                        {/* Targeting Filters Section */}
                        {/* <div className="col-12 mt-40">
                            <h5 className="text-primary-600 fw-bold border-bottom pb-8 mb-24">Targeting Filters (Exhibitors/Visitors)</h5>
                            <div className="row gy-4">
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Target Segment</label>
                                    <Select
                                        options={segmentOptions}
                                        value={segmentOptions.find(opt => opt.value === formData.targetSegment)}
                                        onChange={(val) => handleSelectChange('targetSegment', val)}
                                        styles={selectStyles()}
                                        placeholder="Select Segment"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Visitor Type</label>
                                    <Select
                                        options={visitorTypeOptions}
                                        value={visitorTypeOptions.find(opt => opt.value === formData.visitorType)}
                                        onChange={(val) => handleSelectChange('visitorType', val)}
                                        styles={selectStyles()}
                                        placeholder="Select Type"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold">Target Locations</label>
                                    <Select
                                        isMulti
                                        options={locationOptions}
                                        value={locationOptions.filter(opt => formData.targetLocations.includes(opt.value))}
                                        onChange={(val) => handleSelectChange('targetLocations', val)}
                                        styles={selectStyles()}
                                        placeholder="All Locations"
                                    />
                                </div>
                            </div>
                        </div> */}

                        {/* Buttons */}
                        <div className="col-12 text-end mt-4">
                            <Link to="/flash-news" className="btn btn-outline-secondary radius-8 px-32 me-3 fw-bold">Cancel</Link>
                            <button
                                type="submit"
                                className="btn radius-8 px-32 fw-bold"
                                disabled={submitting}
                                style={{ backgroundColor: "#ed1c24", color: "white" }}
                            >
                                {submitting ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default FlashNewsFormLayer;
